import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Advertisement } from '@/types';
import { useCommunityContext } from '@/providers/community-context';
import { AdContentForm } from './components/AdContentForm';
import type { AdContentFormValues } from './components/ad-content-schema';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle as CardHeaderTitle,
} from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import useSWR from 'swr';

export default function EditAdvertisementPage() {
  const navigate = useNavigate();
  const { community } = useCommunityContext();
  const { adId } = useParams<{ adId: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, isLoading, mutate } = useSWR(
    community?.id ? `/api/communities/${community.id}/advertisements` : null,
    (url) => axiosInstance.get(url).then((res) => res.data)
  );

  const ad = data?.advertisements?.find((a: Advertisement) => a.id === adId);

  const initialValues = ad
    ? {
        title: ad.title || '',
        notes: ad.description || '',
        redirectUrl: ad.redirectUrl || '',
      }
    : undefined;

  const onSubmit = async (formDataValues: AdContentFormValues) => {
    if (!community?.id || !adId) return;

    try {
      setIsSubmitting(true);
      const payload = {
        title: formDataValues.title,
        notes: formDataValues.notes,
        redirectUrl: formDataValues.redirectUrl,
      };

      const response = await axiosInstance.patch(
        `/api/communities/${community.id}/advertisements/${adId}`,
        payload
      );

      if (response.data.success) {
        toast.success('Ad updated successfully');
        mutate();
        navigate(`/${community.id}/advertisement`);
      }
    } catch (error) {
      console.error('Error saving ad:', error);
      toast.error('Failed to update ad');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!ad) {
    return <div className="text-center p-12 text-muted-foreground">Advertisement not found.</div>;
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <Card className="w-full border-0 bg-card shadow-none md:shadow-2xl animate-in slide-in-from-bottom-5 duration-300">
        <CardHeader className="px-3 md:px-6">
          <CardHeaderTitle className="text-2xl font-black tracking-tight text-foreground">
            Edit Advertisement
          </CardHeaderTitle>
          <CardDescription className="text-muted-foreground">
            Update the creative content. Campaign schedule and placement are edited separately.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-3 md:px-6">
          <AdContentForm
            initialValues={initialValues}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            onCancel={() => navigate(-1)}
            isEditing
          />
        </CardContent>
      </Card>
    </div>
  );
}
